import React, { useRef } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Badge } from "reactstrap";
import { useReactToPrint } from "react-to-print";
import { useTenant } from "../../hooks/useTenant";
import { formatCurrency } from "../../utils/format";
import PrintableReceipt from "./PrintableReceipt";

/**
 * SalesReceiptModal
 * Modal de recibo exibido após a finalização de uma venda.
 * Mostra o resumo da venda com itens, pagamentos e totais.
 */
const SalesReceiptModal = ({ isOpen, toggle, saleData, memberName }) => {
    const { tenantName, tenantLogo } = useTenant();
    const printRef = useRef();

    const handlePrint = useReactToPrint({
        contentRef: printRef, // Support para v3
        content: () => printRef.current, // Support backward v2
        documentTitle: `Recibo_${saleData?.saleNumber || "Venda"}`
    });

    if (!saleData) return null;



    return (
        <Modal isOpen={isOpen} toggle={toggle} centered size="md">
            <ModalHeader toggle={toggle} className="border-0 pb-0">
                <div className="d-flex align-items-center gap-2">
                    <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: "rgba(46, 204, 113, 0.1)"
                        }}
                    >
                        <i className="mdi mdi-check-circle text-success font-size-22"></i>
                    </div>
                    <div>
                        <h5 className="mb-0 fw-bold text-success">Venda Realizada!</h5>
                        <p className="text-muted font-size-12 mb-0">
                            Nº {saleData.saleNumber}
                        </p>
                    </div>
                </div>
            </ModalHeader>

            <ModalBody className="pt-3">
                {/* Dados do membro */}
                <div className="d-flex align-items-center mb-3 p-2 bg-light rounded">
                    <i className="mdi mdi-account-circle text-primary font-size-24 me-2"></i>
                    <div>
                        <span className="fw-bold font-size-13">{memberName || saleData.memberName || "Aluno"}</span>
                        {saleData.memberNumber && (
                            <span className="text-muted font-size-11 ms-2">ID: {saleData.memberNumber}</span>
                        )}
                    </div>
                </div>

                {/* Itens */}
                {saleData.items && saleData.items.length > 0 && (
                    <div className="mb-3">
                        <label className="text-muted font-size-11 fw-bold text-uppercase mb-2 d-block">
                            Itens da Venda
                        </label>
                        <div className="d-flex flex-column gap-1">
                            {saleData.items.map((item, idx) => (
                                <div key={idx} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <div>
                                        <span className="font-size-13 fw-medium">{item.name}</span>
                                        <Badge color="light" className="text-muted font-size-10 ms-2">
                                            {item.type === "membership" ? "Plano" : item.type === "product" ? "Produto" : "Serviço"}
                                        </Badge>
                                    </div>
                                    <span className="fw-bold font-size-13">
                                        {formatCurrency(item.totalPrice || item.unitPrice)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Pagamentos */}
                {saleData.paymentMethods && saleData.paymentMethods.length > 0 && (
                    <div className="mb-3">
                        <label className="text-muted font-size-11 fw-bold text-uppercase mb-2 d-block">
                            Pagamentos
                        </label>
                        <div className="d-flex flex-column gap-1">
                            {saleData.paymentMethods.map((p, idx) => (
                                <div key={idx} className="d-flex justify-content-between align-items-center py-1">
                                    <span className="font-size-12 text-muted">
                                        {p.methodLabel || "Pagamento"}
                                        {p.brand && ` (${p.brand})`}
                                    </span>
                                    <span className="fw-bold text-success font-size-13">
                                        {formatCurrency(p.value)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Totais */}
                <div className="border-top pt-3 mt-2">
                    {saleData.discountTotal > 0 && (
                        <div className="d-flex justify-content-between mb-1 text-danger font-size-12">
                            <span>Desconto</span>
                            <span>- {formatCurrency(saleData.discountTotal)}</span>
                        </div>
                    )}
                    <div className="d-flex justify-content-between fw-bold font-size-16">
                        <span>Total Venda</span>
                        <span className="text-dark">{formatCurrency(saleData.totalAmount)}</span>
                    </div>
                    {saleData.totalPaid > 0 && (
                        <div className="d-flex justify-content-between fw-bold font-size-15 mt-1 text-success">
                            <span>Total Pago</span>
                            <span>{formatCurrency(saleData.totalPaid)}</span>
                        </div>
                    )}
                    {saleData.balance > 0 && (
                        <div className="d-flex justify-content-between mt-1 text-warning font-size-12">
                            <span>Saldo Restante</span>
                            <span>{formatCurrency(saleData.balance)}</span>
                        </div>
                    )}
                </div>
            </ModalBody>

            <ModalFooter className="border-0 pt-0 d-flex flex-row align-items-center gap-2">
                <Button color="success" className="fw-bold m-0 flex-grow-1" onClick={toggle}>
                    <i className="mdi mdi-check me-1"></i>
                    Concluir Venda
                </Button>
                <Button 
                    color="primary" 
                    className="d-flex align-items-center justify-content-center rounded-2 shadow-sm p-0 m-0 border-0 flex-shrink-0" 
                    style={{ width: "38px", height: "38px" }}
                    onClick={handlePrint}
                    title="Imprimir Recibo"
                >
                    <i className="mdi mdi-printer font-size-18"></i>
                </Button>
            </ModalFooter>

            {/* Recibo oculto para ser acionado pelo ReactToPrint */}
            <div style={{ display: "none" }}>
                <div ref={printRef}>
                    <PrintableReceipt
                        saleData={saleData}
                        memberName={memberName}
                        tenantName={tenantName}
                        tenantLogo={tenantLogo}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default SalesReceiptModal;
